<template>
    <div class="bg-white border max-w-2xl max-w-xl mb-5 mx-auto rounded-md shadow-lg w-full border border-gray-300">
        <div class="bg-gray-100 border-b border-gray-300 flex px-4 py-3 rounded-t-md">
            <div class="block w-full md:w-auto">
                <Link :href="incidentLink">
                    <h1 class="text-xl font-semibold mb-1">{{ incident.title }}</h1>
                </Link>
                <p class="text-sm text-gray-400">
                    {{ incident.last_update ? $t("incidents.last_update") : $t("incidents.incident_created") }}
                    <span class="text-flooris-black">
                        {{ incident.last_update ? incident.last_update.created_at : incident.created_at }}
                    </span>
                </p>
                <p class="text-sm text-gray-400">
                    {{ $t("incidents.created_by") }}
                    <span class="text-flooris-black">{{ incident.created_by.name }}</span>
                </p>
            </div>

            <div class="ml-auto items-center flex">
                <div
                    v-if="!incident.resolved"
                    class="px-4 py-1 mr-2 my-auto rounded-md font-semibold border-solid border-2"
                    :class="priorityColor()"
                >
                    {{ $t(textPriority) }}
                </div>
                <div
                    v-if="incident.resolved"
                    class="
                        px-4
                        py-1
                        mr-2
                        my-auto
                        rounded-md
                        font-semibold
                        border-solid border-2 border-green-400
                        text-green-400
                    "
                >
                    Incident Opgelost
                </div>
            </div>

            <div class="flex px-4">
                <dropdown class="my-auto" align="left">
                    <template #trigger>
                        <svg
                            role="img"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 192 512"
                            class="h-4 cursor-pointer text-gray-900 hover:text-gray-500"
                        >
                            <path
                                fill="currentColor"
                                d="M96 184c39.8 0 72 32.2 72 72s-32.2 72-72 72-72-32.2-72-72 32.2-72 72-72zM24 80c0 39.8 32.2 72 72 72s72-32.2 72-72S135.8 8 96 8 24 40.2 24 80zm0 352c0 39.8 32.2 72 72 72s72-32.2 72-72-32.2-72-72-72-72 32.2-72 72z"
                            ></path>
                        </svg>
                    </template>
                    <template #content>
                        <div>
                            <Link
                                class="
                                    block
                                    px-4
                                    py-2
                                    text-sm
                                    leading-5
                                    text-gray-700
                                    bg-white
                                    hover:bg-gray-100
                                    focus:outline-none
                                    focus:bg-gray-100
                                    transition
                                    duration-150
                                    ease-in-out
                                "
                                :href="
                                    route('universal.incidents.show', {
                                        incident: incident.id,
                                    })
                                "
                            >
                                {{ $t("incidents.links.view_incident") }}
                            </Link>
                        </div>
                        <div>
                            <div
                                class="
                                    block
                                    px-4
                                    py-2
                                    text-sm
                                    leading-5
                                    text-gray-700
                                    hover:bg-gray-100
                                    focus:outline-none
                                    focus:bg-gray-100
                                    transition
                                    duration-150
                                    ease-in-out
                                "
                                @click="copyToClipboard(incidentLink)"
                            >
                                {{ $t("incidents.links.copy_url") }}
                            </div>
                        </div>
                        <div class="border-t border-gray-300"></div>
                        <div>
                            <Link
                                v-if="!tenant"
                                class="
                                    block
                                    px-4
                                    py-2
                                    text-sm
                                    leading-5
                                    text-gray-700
                                    hover:bg-gray-100
                                    focus:outline-none
                                    focus:bg-gray-100
                                    transition
                                    duration-150
                                    ease-in-out
                                "
                                :href="
                                    route('universal.incidents.edit', {
                                        incident: incident.id,
                                    })
                                "
                            >
                                {{ $t("incidents.links.edit_incident") }}
                            </Link>
                        </div>
                    </template>
                </dropdown>
            </div>
        </div>
        <div class="py-5 px-4">
            <div class="relative">
                {{ incident.description }}
            </div>
        </div>
        <div v-if="showTimeline" class="p-4 border-t border-gray-300">
            <div v-if="incident.timeline.length === 0" class="flex">
                <p class="text-gray-400 mx-auto my-4 text-lg">
                    {{ $t("incidents.no_update_yet") }}
                </p>
            </div>
            <div v-else>
                <div v-for="(update, index) in incident.timeline" :key="update.id" class="my-2 flex">
                    <div class="flex flex-col mr-4">
                        <status-icon class="my-auto" :icon="update.icon"></status-icon>

                        <div class="flex-grow w-1 pt-2 mx-auto">
                            <div
                                v-show="index !== incident.timeline.length - 1"
                                class="h-full rounded-full bg-gray-300"
                            ></div>
                        </div>
                    </div>
                    <div class="block">
                        <p class="">{{ update.title }}</p>
                        <p class="text-sm text-gray-400">
                            {{ update.created_at }}
                        </p>
                        <p class="text-sm">{{ update.description }}</p>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="incident.project_checks.sensu.length" class="p-4 border-t border-gray-300 bg-gray-100 rounded-md">
            <div class="justify-between" :class="showSensuProjectCheck ? '' : 'flex'">
                <h3 class="p-2 text-md my-auto mx-3 text-center font-bold">Sensu Intergrations</h3>
                <div
                    v-if="!showSensuProjectCheck"
                    class="hover:text-gray-500 my-auto text-sm text-gray-800 cursor-pointer"
                    @click="showSensuProjectCheck = true"
                >
                    <p>show more</p>
                </div>
            </div>
            <div v-if="showSensuProjectCheck" class="">
                <div v-for="(event, index) in sensuProjectChecksReferences" :key="index" class="px-4 pt-5 sm:px-6">
                    <div class="font-semibold mb-4 sm:col-span-1">
                        {{ event.name ?? `${event.check} ${event.entity}` }}
                    </div>
                    <div class="mt-1 text-sm sm:mt-0 sm:col-span-1">
                        {{ event.description ?? event["statusOutput"] }}
                    </div>
                    <div
                        v-if="index !== sensuProjectChecksReferences.length - 1"
                        class="h-1 w-full bg-gray-200 my-3"
                    ></div>
                </div>
                <div class="flex justify-end mt-2 pr-4">
                    <div class="hover:text-gray-500 cursor-pointer" @click="showSensuProjectCheck = false">
                        <p>show less</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Dropdown from "@/Jetstream/Dropdown";
import StatusIcon from "@/Components/StatusIcon";
import { computed, ref } from "vue";
import { Link } from "@inertiajs/inertia-vue3";

export default {
    name: "Incident",
    components: { StatusIcon, Dropdown, Link },
    props: {
        incident: {
            type: Object,
            required: true,
        },
        showTimeline: {
            type: Boolean,
            default: false,
            required: false,
        },
        tenant: {
            type: String,
            default: null,
            required: false,
        },
    },
    setup(props) {
        const priorityColor = () => {
            switch (props.incident.priority) {
                case "Low":
                    return "border-lightblue-500 text-lightblue-500 text-white";
                case "Medium":
                    return "border-red-400 text-red-400";
                case "High":
                    return "border-flooris-black text-black";
                default:
                    return "border-lightblue-500 text-lightblue-500";
            }
        };

        const textPriority = computed(() => {
            switch (props.incident.priority) {
                case "Low":
                    return "incidents.forms.priority.items.low";
                case "Medium":
                    return "incidents.forms.priority.items.medium";
                case "High":
                    return "incidents.forms.priority.items.high";
                default:
                    return "None";
            }
        });

        const incidentLink = computed(() => {
            if (!props.tenant) {
                return route("universal.incidents.show", {
                    incident: props.incident.id,
                });
            }

            return route("tenant.incidents.show", {
                incident: props.incident.id,
                tenant: props.tenant,
            });
        });

        const showMoreEnabled = ref(false);
        const showSensuProjectCheck = ref(false);
        const enableShowMore = () => {
            showMoreEnabled.value = true;
        };
        const showMore = computed(() => (!showMoreEnabled.value ? " overflow-hidden overflow-ellipsis" : ""));

        const copyToClipboard = (str) => {
            const el = document.createElement("textarea");
            el.value = str;
            document.body.appendChild(el);
            el.select();
            document.execCommand("copy");
            document.body.removeChild(el);
        };

        const sensuProjectChecksReferences = computed(() => {
            const noDuplicateChecks = ref([]);
            const sensuChecks = props.incident.project_checks.sensu.map((check) => {
                if (check.checkable_references) return check.checkable_references;
                return check.id;
            });
            sensuChecks.forEach((check, index) => {
                if (sensuChecks.indexOf(check) === index) {
                    noDuplicateChecks.value.push(props.incident.project_checks.sensu[index]);
                }
            });
            return noDuplicateChecks.value;
        });

        return {
            priorityColor,
            copyToClipboard,
            enableShowMore,
            showSensuProjectCheck,
            sensuProjectChecksReferences,
            incidentLink,
            textPriority,
            showMore,
            showMoreEnabled,
        };
    },
};
</script>
